import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useLoading } from 'react-simplikit';

import type { SavedList } from '@/entities/saved-creators-list';
import type { SelectedCreatorListSummary } from '@/features/campaign/hooks';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import { cn } from '@/shared/utils';
import { formatNumber } from '@/shared/utils/format-number';

type Props = {
  onImportCreatorsFromSavedList: (list: SavedList) => Promise<void>;
  remainingSlots: number;
  savedLists: SavedList[];
  savedListsUnavailable: boolean;
  selectedCreatorLists: SelectedCreatorListSummary[];
};

export function CampaignCreatorSavedListPicker({
  onImportCreatorsFromSavedList,
  remainingSlots,
  savedLists,
  savedListsUnavailable,
  selectedCreatorLists,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isImporting, startLoading] = useLoading();
  const availableLists = savedLists;

  const handleSelect = async (listId: string) => {
    const selectedList = availableLists.find((list) => list.id === listId);
    if (!selectedList) return;
    setIsOpen(false);
    await startLoading(onImportCreatorsFromSavedList(selectedList));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={isOpen}
          tabIndex={
            isImporting ||
            savedListsUnavailable ||
            availableLists.length === 0 ||
            remainingSlots === 0
              ? -1
              : 0
          }
          aria-disabled={
            isImporting ||
            savedListsUnavailable ||
            availableLists.length === 0 ||
            remainingSlots === 0
          }
          className={cn(
            'border-border bg-card flex min-h-10 flex-1 items-center justify-between rounded-xl border px-3 py-2.5 transition-colors',
            isOpen && 'border-foreground/20 bg-muted',
            (isImporting ||
              savedListsUnavailable ||
              availableLists.length === 0 ||
              remainingSlots === 0) &&
              'pointer-events-none opacity-50',
          )}
        >
          <span className="text-muted-foreground truncate text-sm">
            {savedListsUnavailable
              ? 'Saved lists are unavailable'
              : availableLists.length === 0
                ? 'No saved lists in Manage'
                : selectedCreatorLists.length > 0
                  ? 'Search to add more saved lists'
                  : 'Search a saved list'}
          </span>
          <div className="ml-3 flex shrink-0 items-center gap-2">
            <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-105 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search lists" />
          <CommandList>
            <CommandEmpty>No lists found.</CommandEmpty>
            {availableLists.map((list) => (
              <CommandItem
                key={list.id}
                value={`${list.title} ${list.total_count}`}
                onSelect={() => {
                  void handleSelect(list.id);
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{list.title}</div>
                  <div className="text-muted-foreground text-xs">
                    {formatNumber(list.total_count)} creators
                  </div>
                </div>
                {selectedCreatorLists.some((selectedList) => selectedList.id === list.id) ? (
                  <Check className="text-foreground size-4" />
                ) : null}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
